// Match Lab review controls for one detected event: confirm, reject (with
// a reason and note), correct (team / time / type) and undo. Each action
// is one append-only raw_assertion row on the server
// (POST /api/v1/opsqa/games/<id>/event-reviews, app/layerstack/event_review.py).
import { useState } from 'react'
import type { CSSProperties, MouseEvent } from 'react'

import type { PitchEvent } from './MatchLabPitch'

/** Each kind's correctable type field and its values (event tables' CHECK
 *  vocabularies, migration 087 — the server validates the same lists). */
export const TYPE_FIELD: Record<PitchEvent['kind'], { field: string; values: string[] }> = {
  goal: { field: 'goal_type',
          values: ['open_play', 'set_piece', 'penalty', 'own_goal'] },
  shot: { field: 'outcome',
          values: ['goal', 'saved', 'blocked', 'off_target', 'woodwork'] },
  set_piece: { field: 'restart_type',
               values: ['kick_off', 'throw_in', 'goal_kick', 'corner',
                        'free_kick', 'penalty'] },
}

const btn: CSSProperties = {
  background: 'none', border: '1px solid #444', borderRadius: '3px',
  color: '#bbb', fontSize: '10px', lineHeight: 1.2, padding: '0 4px',
  cursor: 'pointer',
}

const stop = (e: MouseEvent) => e.stopPropagation()

export function ReviewBadge({ event }: { event: PitchEvent }) {
  const rv = event.review as ({ action: string; matched_by?: string } | null | undefined)
  if (!rv) return null
  const text = rv.action === 'reject' ? 'Rejected'
    : rv.action === 'correct' ? 'Corrected' : 'Confirmed'
  const color = rv.action === 'reject' ? '#e53e3e'
    : rv.action === 'correct' ? '#d69e2e' : '#38a169'
  return (
    <span data-review={rv.action}
          title={rv.matched_by === 'anchor'
            ? 'Reviewed before the events were rebuilt; matched by team and time'
            : undefined}
          style={{ fontSize: '9px', fontWeight: 600, color }}>
      {text}{rv.matched_by === 'anchor' ? '*' : ''}
    </span>
  )
}

export function ReviewControls({ event, mode, onMode, onSubmit, currentFrame,
                                 homeTeamId, awayTeamId, homeName, awayName }: {
  event: PitchEvent
  /** Which inline form is open for THIS event, if any. */
  mode: 'reject' | 'correct' | null
  onMode: (mode: 'reject' | 'correct' | null) => void
  onSubmit: (body: Record<string, unknown>) => void
  currentFrame: number
  homeTeamId: number | null
  awayTeamId: number | null
  homeName: string
  awayName: string
}) {
  const rv = event.review as ({ assertion_id: number; action: string } | null | undefined)
  return (
    <div onClick={stop} style={{ marginTop: '2px' }}>
      <div style={{ display: 'flex', gap: '3px' }}>
        {rv ? (
          <button style={btn} title="Undo this review"
                  onClick={() => onSubmit({ action: 'undo',
                                            assertion_id: rv.assertion_id })}>
            {'↶'} Undo
          </button>
        ) : (
          <>
            <button style={btn} title="Confirm: the event is right"
                    onClick={() => onSubmit({ action: 'confirm' })}>
              {'✓'}
            </button>
            <button style={btn} title="Reject: this event is wrong"
                    onClick={() => onMode(mode === 'reject' ? null : 'reject')}>
              {'✗'}
            </button>
            <button style={btn} title="Correct team, time or type"
                    onClick={() => onMode(mode === 'correct' ? null : 'correct')}>
              {'✎'}
            </button>
          </>
        )}
      </div>
      {mode === 'reject' && (
        <RejectForm onCancel={() => onMode(null)}
                    onSubmit={(reason, note) =>
                      onSubmit({ action: 'reject', reason, note: note || undefined })} />
      )}
      {mode === 'correct' && (
        <CorrectForm event={event} currentFrame={currentFrame}
                     homeTeamId={homeTeamId} awayTeamId={awayTeamId}
                     homeName={homeName} awayName={awayName}
                     onCancel={() => onMode(null)}
                     onSubmit={(correction, note) =>
                       onSubmit({ action: 'correct', correction,
                                  note: note || undefined })} />
      )}
    </div>
  )
}

const input: CSSProperties = { fontSize: '10px', padding: '1px 3px',
                               width: '100%', boxSizing: 'border-box' }

function RejectForm({ onSubmit, onCancel }: {
  onSubmit: (reason: 'wrong' | 'other', note: string) => void
  onCancel: () => void
}) {
  const [reason, setReason] = useState<'wrong' | 'other'>('wrong')
  const [note, setNote] = useState('')
  return (
    <div style={{ display: 'grid', gap: '3px', marginTop: '3px' }}>
      <select aria-label="Reject reason" value={reason} style={input}
              onChange={(e) => setReason(e.target.value as 'wrong' | 'other')}>
        <option value="wrong">Didn't happen / wrong event</option>
        <option value="other">Other</option>
      </select>
      <input aria-label="Reject note" placeholder="Note (optional)"
             value={note} maxLength={500} style={input}
             onChange={(e) => setNote(e.target.value)} />
      <div style={{ display: 'flex', gap: '3px' }}>
        <button style={{ ...btn, color: '#e53e3e' }}
                onClick={() => onSubmit(reason, note.trim())}>Reject</button>
        <button style={btn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

function CorrectForm({ event, currentFrame, homeTeamId, awayTeamId,
                       homeName, awayName, onSubmit, onCancel }: {
  event: PitchEvent
  currentFrame: number
  homeTeamId: number | null
  awayTeamId: number | null
  homeName: string
  awayName: string
  onSubmit: (correction: Record<string, string | number>, note: string) => void
  onCancel: () => void
}) {
  const tf = TYPE_FIELD[event.kind]
  const [team, setTeam] = useState<number | null>(event.team_id)
  const [type, setType] = useState<string>(event.detail ?? '')
  const [frame, setFrame] = useState<number>(event.frame_idx)
  const [note, setNote] = useState('')
  // Only what changed is sent: a correction names the fields it fixes.
  const correction: Record<string, string | number> = {}
  if (team != null && team !== event.team_id) correction.team_id = team
  if (type && type !== (event.detail ?? '')) correction[tf.field] = type
  if (frame !== event.frame_idx) correction.frame_idx = frame
  const changed = Object.keys(correction).length > 0
  return (
    <div style={{ display: 'grid', gap: '3px', marginTop: '3px' }}>
      <select aria-label="Corrected team" value={team ?? ''} style={input}
              onChange={(e) => setTeam(Number(e.target.value))}>
        {homeTeamId != null && <option value={homeTeamId}>{homeName}</option>}
        {awayTeamId != null && <option value={awayTeamId}>{awayName}</option>}
      </select>
      <select aria-label={`Corrected ${tf.field.replace('_', ' ')}`}
              value={type} style={input}
              onChange={(e) => setType(e.target.value)}>
        {!event.detail && <option value="">—</option>}
        {tf.values.map((v) => (
          <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>
        ))}
      </select>
      <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
        <span style={{ fontSize: '10px', opacity: 0.7 }}>f{frame}</span>
        <button style={btn} title="Move this event to the video's current frame"
                onClick={() => setFrame(currentFrame)}>
          Use current frame
        </button>
      </div>
      <input aria-label="Correction note" placeholder="Note (optional)"
             value={note} maxLength={500} style={input}
             onChange={(e) => setNote(e.target.value)} />
      <div style={{ display: 'flex', gap: '3px' }}>
        <button style={{ ...btn, color: changed ? '#d69e2e' : '#666' }}
                disabled={!changed}
                onClick={() => onSubmit(correction, note.trim())}>
          Save correction
        </button>
        <button style={btn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}
